import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const STORAGE_KEY = "goodeyes.enquiry.v1";

const EnquiryContext = createContext(null);

const readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export function EnquiryProvider({ children }) {
  const [items, setItems] = useState(readStored);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // storage quota / private mode — ignore
    }
  }, [items]);

  const add = useCallback((line) => {
    // line: { styleSlug, styleName, colourSlug, colourName, strength, qty, img }
    setItems((prev) => {
      const idx = prev.findIndex(
        (p) =>
          p.styleSlug === line.styleSlug &&
          p.colourSlug === line.colourSlug &&
          p.strength === line.strength,
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: Math.min(999, next[idx].qty + line.qty) };
        return next;
      }
      return [...prev, { ...line, qty: Math.min(999, line.qty) }];
    });
  }, []);

  const setQty = useCallback((idx, qty) => {
    setItems((prev) => {
      const next = [...prev];
      const q = Math.max(0, Math.min(999, parseInt(qty || 0, 10) || 0));
      next[idx] = { ...next[idx], qty: q };
      return next.filter((i) => i.qty > 0);
    });
  }, []);

  const remove = useCallback((idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const totalUnits = useMemo(() => items.reduce((n, i) => n + (i.qty || 0), 0), [items]);

  const value = useMemo(
    () => ({ items, add, setQty, remove, clear, totalUnits, open, setOpen }),
    [items, add, setQty, remove, clear, totalUnits, open],
  );

  return <EnquiryContext.Provider value={value}>{children}</EnquiryContext.Provider>;
}

export function useEnquiry() {
  const ctx = useContext(EnquiryContext);
  if (!ctx) throw new Error("useEnquiry must be used within EnquiryProvider");
  return ctx;
}
